export const isViedmaPath = (pathname) => /^\/(?:app|kids|teen)\/viedma(?:\/|$)/.test(pathname);

/**
 * Resolves the active portal for guest/auth pages ('viedma', 'quran', or 'app').
 * Prioritizes explicit URL parameters ('portal' and 'redirect'), followed by storage persistence.
 */
export function resolveGuestPortal(searchParams, storedPortal) {
  const portalParam = typeof searchParams === 'string'
    ? searchParams
    : searchParams?.get?.('portal');

  const redirectParam = typeof searchParams === 'object' && searchParams?.get
    ? searchParams.get('redirect')
    : null;

  const resolvedStored = storedPortal ?? (
    (typeof sessionStorage !== 'undefined' ? (sessionStorage.getItem('login_target_portal') || sessionStorage.getItem('last_dashboard_portal')) : null) ||
    (typeof localStorage !== 'undefined' ? localStorage.getItem('last_dashboard_portal') : null)
  );

  // 1. Quran portal match
  if (portalParam === 'quran' || (!portalParam && (redirectParam?.startsWith('/quran') || resolvedStored === 'quran'))) {
    return 'quran';
  }

  // 2. Viedma portal match
  if (
    portalParam === 'viedma' ||
    portalParam === 'viedma_teen' ||
    portalParam === 'viedma_kids' ||
    (!portalParam && (
      redirectParam?.includes('viedma') ||
      resolvedStored === 'viedma' ||
      resolvedStored === 'viedma_teen' ||
      resolvedStored === 'viedma_kids'
    ))
  ) {
    return 'viedma';
  }

  return 'app';
}

// Shared screens use ordinary app paths internally. Only Viedma routes translate them.
export function toViedmaPath(target, pathname) {
  if (!isViedmaPath(pathname) || target == null || typeof target === 'number') return target;
  if (typeof target === 'object') {
    return target.pathname ? { ...target, pathname: toViedmaPath(target.pathname, pathname) } : target;
  }
  const cleanTarget = target.split(/[?#]/)[0];
  if (isViedmaPath(cleanTarget)) return target;

  // Never translate standalone public/invitation/auth paths to Viedma:
  if (
    cleanTarget === '/app/school-invite' ||
    cleanTarget.startsWith('/app/school-invite/') ||
    cleanTarget === '/school-invite' ||
    cleanTarget.startsWith('/school-invite/') ||
    cleanTarget === '/app/invite' ||
    cleanTarget.startsWith('/app/invite/') ||
    cleanTarget === '/family/invite' ||
    cleanTarget.startsWith('/family/invite/') ||
    cleanTarget.startsWith('/auth/') ||
    cleanTarget.startsWith('/login') ||
    cleanTarget.startsWith('/verify')
  ) {
    return target;
  }

  const isKidsContext =
    /^\/kids\/viedma(?:\/|$)/.test(pathname) ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('last_dashboard_portal') === 'viedma_kids') ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('last_dashboard_portal') === 'viedma_kids');

  const isTeenContext =
    /^\/teen\/viedma(?:\/|$)/.test(pathname) ||
    (typeof sessionStorage !== 'undefined' && sessionStorage.getItem('last_dashboard_portal') === 'viedma_teen') ||
    (typeof localStorage !== 'undefined' && localStorage.getItem('last_dashboard_portal') === 'viedma_teen');

  if (target.startsWith('/teen/')) {
    return target.replace(/^\/teen(?=\/|[?#]|$)/, '/teen/viedma');
  }

  if (target.startsWith('/kids/')) {
    return target.replace(/^\/kids(?=\/|[?#]|$)/, '/kids/viedma');
  }

  if (target.startsWith('/app/kids/')) {
    return target.replace(/^\/app\/kids(?=\/|[?#]|$)/, '/kids/viedma');
  }

  if (isTeenContext && (target.startsWith('/app/books/') || target.startsWith('/books/'))) {
    const bookSubPath = target.replace(/^\/(?:app\/)?books(?=\/|[?#]|$)/, '');
    return `/teen/viedma/books${bookSubPath}`;
  }

  if (isKidsContext && (target.startsWith('/app/books/') || target.startsWith('/books/'))) {
    const bookSubPath = target.replace(/^\/(?:app\/)?books(?=\/|[?#]|$)/, '');
    return `/kids/viedma/books${bookSubPath}`;
  }

  return target.replace(/^\/app(?=\/|[?#]|$)/, '/app/viedma');
}

export function toAppPath(pathname) {
  if (/^\/teen\/viedma(?:\/|$)/.test(pathname)) {
    return pathname.replace(/^\/teen\/viedma(?=\/|$)/, '/teen');
  }
  if (/^\/kids\/viedma(?:\/|$)/.test(pathname)) {
    return pathname.replace(/^\/kids\/viedma(?=\/|$)/, '/kids');
  }
  return isViedmaPath(pathname) ? pathname.replace(/^\/app\/viedma(?=\/|$)/, '/app') : pathname;
}
